import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { ArrowRight, Info, Minus, Plus, RotateCcw } from "lucide-react";
import { useModal } from "@/context/ModalContext";
import {
  COMPONENTS,
  DEFAULT_QUANTITIES,
  TERMS,
  UNIT_VALUE,
  entryPricePerMonth,
  formatMoney,
  monthlyBrv,
  quote,
  setQuantity,
  toMoney,
  type ComponentKey,
  type Quantities,
} from "@/data/pricing";
import { MARKET } from "@/market";

const { showUnit } = MARKET.pricing;

export function Pricing() {
  const { t, i18n } = useTranslation();
  const { open } = useModal();

  const [qty, setQty] = useState<Quantities>(DEFAULT_QUANTITIES);
  const [termIdx, setTermIdx] = useState(TERMS.length - 1);

  const term = TERMS[termIdx];
  const result = useMemo(() => quote(qty, term), [qty, term]);
  const perMonth = monthlyBrv(qty);

  const brvFmt = (value: number) => value.toLocaleString(i18n.language, { maximumFractionDigits: 3 });
  const uzs = (brv: number) => formatMoney(toMoney(brv));

  const monthWord = (months: number) => {
    if (i18n.language === "ru") {
      if (months === 1) return t("pricing.month_one");
      return months < 5 ? t("pricing.month_few") : t("pricing.month_many");
    }
    return months === 1 ? t("pricing.month_one") : t("pricing.month_many");
  };

  const bump = (key: ComponentKey, delta: number) =>
    setQty(current => setQuantity(current, key, (current[key] || 0) + delta));

  const rules = t("pricing.rules", { returnObjects: true }) as string[];
  const notes = t("pricing.notes", { returnObjects: true }) as string[];

  return (
    <section id="pricing" className="bg-gradient-to-b from-white to-gray-50 py-[96px] overflow-hidden">
      <div className="mx-auto max-w-[1200px] px-4 sm:px-6">
        {/* ── Header ── */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center"
        >
          <p className="text-[11px] font-semibold tracking-[0.12em] uppercase text-primary mb-3">
            {t("pricing.tag")}
          </p>
          <h2 className="text-[32px] sm:text-[48px] leading-[1.15] font-bold tracking-[-0.02em] text-foreground">
            {t("pricing.title")}
          </h2>
          <p className="mt-4 text-foreground-muted text-[17px] sm:text-[18px] max-w-[620px] mx-auto leading-relaxed">
            {t("pricing.subtitle")}
          </p>

          <div className="mt-7 inline-flex flex-col items-center">
            <div className="flex items-baseline gap-1.5">
              <span className="text-[15px] text-foreground/45">{t("pricing.from_label")}</span>
              <span className="text-[38px] sm:text-[44px] font-bold tracking-tight text-foreground font-raleway leading-none">
                {formatMoney(entryPricePerMonth())}
              </span>
              <span className="text-[14px] text-foreground/45">{t("pricing.per_month")}</span>
            </div>
            <p className="mt-1.5 text-[13px] text-foreground/40">{t("pricing.from_note")}</p>
          </div>
        </motion.div>

        <div className="mt-14 grid lg:grid-cols-[minmax(0,1fr)_336px] gap-8 items-start">
          {/* ── Left column: term + components + matrix ── */}
          <div className="min-w-0 space-y-8">
            {/* Term selector */}
            <div className="rounded-2xl border border-foreground/10 bg-white p-5 shadow-sm">
              <div className="flex items-baseline justify-between gap-3">
                <p className="text-[11px] font-bold tracking-[0.12em] uppercase text-foreground/45">
                  {t("pricing.term_title")}
                </p>
                <p className="text-[12px] text-foreground/35">{t("pricing.term_note")}</p>
              </div>

              <div className="mt-4 grid grid-cols-3 gap-2.5">
                {TERMS.map((option, idx) => {
                  const active = idx === termIdx;
                  return (
                    <button
                      key={option.months}
                      onClick={() => setTermIdx(idx)}
                      aria-pressed={active}
                      className={`rounded-xl border px-2 py-3.5 text-center transition-all ${
                        active
                          ? "border-primary bg-primary text-white shadow-sm"
                          : "border-foreground/10 bg-white text-foreground hover:border-primary/40"
                      }`}
                    >
                      <span className="block text-[22px] font-bold font-raleway leading-none">
                        {option.months}
                      </span>
                      <span
                        className={`mt-1 block text-[11px] leading-tight ${
                          active ? "text-white/80" : "text-foreground/45"
                        }`}
                      >
                        {monthWord(option.months)}
                        <br />
                        −{Math.round(option.discount * 100)}%
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Component picker */}
            <div className="rounded-2xl border border-foreground/10 bg-white p-5 shadow-sm">
              <div className="flex items-center justify-between gap-3">
                <p className="text-[11px] font-bold tracking-[0.12em] uppercase text-foreground/45">
                  {t("pricing.build_title")}
                </p>
                <button
                  onClick={() => setQty(DEFAULT_QUANTITIES)}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-foreground/10 px-3 py-1.5 text-[12px] text-foreground/55 transition-colors hover:border-primary/40 hover:text-foreground"
                >
                  <RotateCcw className="h-3 w-3" />
                  {t("pricing.reset")}
                </button>
              </div>

              <p className="mt-2 text-[12.5px] leading-relaxed text-foreground/40">
                {t("components.paid_note")}
              </p>

              <ul className="mt-4 divide-y divide-foreground/[0.07]">
                {COMPONENTS.map(component => {
                  const n = qty[component.key] || 0;
                  const lineBrv = component.brv * n;
                  const atFloor = n <= (component.min ?? 0);
                  const atCap = component.perBranch === true && n >= qty.branches;

                  return (
                    <li
                      key={component.key}
                      className={`grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-4 gap-y-2 py-3.5 sm:grid-cols-[minmax(0,1fr)_auto_auto] ${
                        n > 0 ? "" : "opacity-60"
                      }`}
                    >
                      {/* Name + unit price */}
                      <div className="min-w-0">
                        <p className="text-[14.5px] font-semibold text-foreground leading-snug">
                          {t(`components.items.${component.key}.name`)}
                        </p>
                        <p className="mt-0.5 text-[12px] text-foreground/40 leading-relaxed">
                          {showUnit && `${brvFmt(component.brv)} ${t("pricing.brv_unit")} · `}
                          {uzs(component.brv)} {t("pricing.per_month")}
                          {component.perBranch && ` · ${t("components.per_branch")}`}
                          {component.min ? ` · ${t("components.min_one")}` : ""}
                        </p>
                      </div>

                      {/* Stepper */}
                      <div className="inline-flex items-center gap-0.5 rounded-xl border border-foreground/10 bg-[#F8FAFC] p-0.5 justify-self-end">
                        <button
                          onClick={() => bump(component.key, -1)}
                          disabled={atFloor}
                          aria-label={`− ${t(`components.items.${component.key}.name`)}`}
                          className="flex h-8 w-8 items-center justify-center rounded-lg text-foreground transition-colors hover:bg-white disabled:text-foreground/20 disabled:hover:bg-transparent"
                        >
                          <Minus className="h-3.5 w-3.5" />
                        </button>
                        <span className="min-w-[28px] text-center text-[14px] font-semibold tabular-nums">
                          {n}
                        </span>
                        <button
                          onClick={() => bump(component.key, 1)}
                          disabled={atCap}
                          aria-label={`+ ${t(`components.items.${component.key}.name`)}`}
                          className="flex h-8 w-8 items-center justify-center rounded-lg text-foreground transition-colors hover:bg-white disabled:text-foreground/20 disabled:hover:bg-transparent"
                        >
                          <Plus className="h-3.5 w-3.5" />
                        </button>
                      </div>

                      {/* Line total */}
                      <div className="col-span-2 text-right text-[13px] tabular-nums text-foreground/55 sm:col-span-1 sm:min-w-[120px]">
                        {lineBrv > 0 ? (
                          <>
                            <span className="font-semibold text-foreground">{uzs(lineBrv)}</span>
                            <span className="text-foreground/40"> {t("pricing.per_month")}</span>
                          </>
                        ) : (
                          <span className="text-foreground/25">—</span>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>

            {/* Term comparison */}
            <div className="rounded-2xl border border-foreground/10 bg-white p-5 shadow-sm">
              <p className="text-[11px] font-bold tracking-[0.12em] uppercase text-foreground/45">
                {t("pricing.matrix_title")}
              </p>

              <div className="mt-4 overflow-x-auto">
                <table className="w-full min-w-[420px] border-collapse text-left">
                  <thead>
                    <tr className="border-b border-foreground/10">
                      {["col_term", "col_discount", "col_per_month", "col_total"].map((col, i) => (
                        <th
                          key={col}
                          className={`pb-2.5 text-[11px] font-medium uppercase tracking-[0.08em] text-foreground/40 ${
                            i > 0 ? "text-right" : ""
                          }`}
                        >
                          {t(`pricing.${col}`)}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {TERMS.map((option, idx) => {
                      const row = quote(qty, option);
                      const active = idx === termIdx;
                      return (
                        <tr
                          key={option.months}
                          onClick={() => setTermIdx(idx)}
                          className={`cursor-pointer border-b border-foreground/[0.06] transition-colors last:border-0 ${
                            active ? "bg-primary/[0.06]" : "hover:bg-foreground/[0.02]"
                          }`}
                        >
                          <td className="py-3 text-[14px] font-medium text-foreground">
                            {option.months} {monthWord(option.months)}
                          </td>
                          <td className="py-3 text-right text-[13px] font-semibold text-primary tabular-nums">
                            −{Math.round(option.discount * 100)}%
                          </td>
                          <td className="py-3 text-right text-[15px] font-semibold text-foreground tabular-nums">
                            {uzs(row.discountedPerMonthBrv)}
                          </td>
                          <td className="py-3 text-right text-[13px] text-foreground/50 tabular-nums">
                            {uzs(row.totalBrv)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* ── Right column: the quote ── */}
          <motion.aside
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="lg:sticky lg:top-24"
          >
            <div className="rounded-2xl border border-foreground/10 bg-white p-6 shadow-lg">
              <div className="flex items-baseline justify-between border-b-2 border-foreground/80 pb-2.5">
                <p className="text-[13px] font-bold tracking-[0.08em] text-foreground">
                  {t("pricing.receipt_title")}
                </p>
                <p className="text-[11px] tabular-nums text-foreground/45">
                  {term.months} {monthWord(term.months)} · −{Math.round(term.discount * 100)}%
                </p>
              </div>

              {/* Line items */}
              <div className="border-b border-dashed border-foreground/15 py-3 text-[12.5px]">
                {perMonth > 0 ? (
                  COMPONENTS.filter(c => (qty[c.key] || 0) > 0).map(c => (
                    <div key={c.key} className="flex justify-between gap-3 py-[3px]">
                      <span className="min-w-0 truncate text-foreground/60">
                        {t(`components.items.${c.key}.name`)} ×{qty[c.key]}
                      </span>
                      <span className="shrink-0 tabular-nums text-foreground/75">
                        {uzs(c.brv * qty[c.key])}
                      </span>
                    </div>
                  ))
                ) : (
                  <div className="flex justify-between text-foreground/40">
                    <span>{t("pricing.receipt_empty")}</span>
                    <span>—</span>
                  </div>
                )}
              </div>

              <dl className="pt-3 text-[13px]">
                <div className="flex justify-between gap-3 py-1">
                  <dt className="text-foreground/55">{t("pricing.receipt_base")}</dt>
                  <dd className="tabular-nums text-foreground/75">{uzs(perMonth)}</dd>
                </div>
                <div className="mt-1.5 flex justify-between gap-3 border-t border-dashed border-foreground/15 pt-2.5">
                  <dt className="text-foreground/55">
                    {t("pricing.receipt_full", { months: term.months })}
                  </dt>
                  <dd className="tabular-nums text-foreground/75">{uzs(result.fullBrv)}</dd>
                </div>
                <div className="flex justify-between gap-3 py-1">
                  <dt className="text-foreground/45">{t("pricing.receipt_discount")}</dt>
                  <dd className="tabular-nums font-semibold text-primary">
                    −{Math.round(term.discount * 100)}%
                  </dd>
                </div>
              </dl>

              {/* Total */}
              <div className="mt-4">
                <p className="font-raleway text-[32px] font-bold leading-none tracking-tight text-foreground tabular-nums">
                  {uzs(result.totalBrv)}
                </p>
                <p className="mt-1 text-[13px] text-foreground/45">
                  {t("pricing.receipt_total_sub", { months: term.months })}
                  {showUnit &&
                    ` · ${brvFmt(Number(result.totalBrv.toFixed(2)))} ${t("pricing.brv_unit")}`}
                </p>
              </div>

              <div className="mt-4 flex justify-between gap-3 border-t border-dashed border-foreground/15 pt-3 text-[13px]">
                <span className="text-foreground/55">{t("pricing.receipt_per_month")}</span>
                <span className="font-semibold tabular-nums text-foreground">
                  {uzs(result.discountedPerMonthBrv)}
                </span>
              </div>
              <div className="flex justify-between gap-3 py-1 text-[13px]">
                <span className="text-foreground/45">{t("pricing.receipt_save")}</span>
                <span className="tabular-nums text-primary">
                  {result.savedBrv > 0 ? uzs(result.savedBrv) : "—"}
                </span>
              </div>

              <button
                onClick={open}
                className="group mt-5 flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-primary text-[15px] font-semibold text-white shadow-sm transition-all hover:bg-primary-dark hover:shadow-md"
              >
                {t("pricing.cta")}
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </button>

              <p className="mt-3 text-center text-[12px] leading-relaxed text-foreground/40">
                {t("pricing.cta_note")}
              </p>

              {showUnit && (
                <p className="mt-4 rounded-lg border border-primary/20 bg-primary/[0.05] px-3 py-2.5 text-[11.5px] leading-relaxed text-foreground/55">
                  {t("pricing.brv_note", { value: formatMoney(UNIT_VALUE) })}
                </p>
              )}
            </div>
          </motion.aside>
        </div>

        {/* ── Rules & what is billed separately ── */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="mt-12 grid gap-6 md:grid-cols-2"
        >
          <div className="rounded-2xl border border-foreground/10 bg-white p-6">
            <p className="text-[11px] font-bold tracking-[0.12em] uppercase text-foreground/45">
              {t("pricing.rules_title")}
            </p>
            <ol className="mt-4 space-y-2.5">
              {rules.map((rule, i) => (
                <li key={i} className="flex gap-2.5 text-[13.5px] leading-relaxed text-foreground/65">
                  <span className="mt-[3px] flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-foreground/[0.07] text-[10px] font-bold text-foreground/50">
                    {i + 1}
                  </span>
                  {rule}
                </li>
              ))}
            </ol>
          </div>

          <div className="rounded-2xl border border-accent/30 bg-accent/[0.06] p-6">
            <p className="flex items-center gap-2 text-[11px] font-bold tracking-[0.12em] uppercase text-foreground/55">
              <Info className="h-3.5 w-3.5" />
              {t("pricing.notes_title")}
            </p>
            <ul className="mt-4 space-y-3">
              {notes.map((note, i) => (
                <li key={i} className="text-[13.5px] leading-relaxed text-foreground/70">
                  {note}
                </li>
              ))}
            </ul>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
